import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

export default function ArticleDetail() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await api.get(`/articles/${id}`);
        setArticle(response.data);
      } catch (err) {
        console.error("Failed to fetch article:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-[#1B2A4A]/60">Article not found.</p>
        <Link to="/insights" className="text-[#C9A84C] font-semibold text-sm">Back to Insights</Link>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={`Dr. Ben Magee, ${article.category}`}
        type="article"
      />
      {/* HERO */}
      <section className="bg-[#1B2A4A] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#C9A84C] mb-8 transition-colors" data-testid="article-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to Insights
          </Link>
          <Badge className="bg-[#C9A84C] text-[#1B2A4A] rounded-sm mb-4 hover:bg-[#C9A84C]">{article.category}</Badge>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight" data-testid="article-title">
            {article.title}
          </h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-white/50">
            <span>{article.author}</span>
            <span>&bull;</span>
            <span>{new Date(article.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="py-16 md:py-24 bg-white" data-testid="article-body">
        <article className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-lg text-[#1B2A4A]/80 italic mb-8 leading-relaxed">{article.excerpt}</p>
          <div className="prose">
            {article.body.split("\n\n").map((para, idx) => (
              <p key={`para-${idx}-${para.substring(0, 20)}`} className="text-base text-[#1B2A4A]/70 leading-relaxed mb-6">{para}</p>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
}
